import { createAction, props } from '@ngrx/store';
import { User } from '@lmc-app/shared/interface/user.interface';

/**
 * Login Actions
 *
 * Flow:
 * 1. Component dispatches login({ credentials })
 * 2. Effect calls backend login API
 * 3. Backend sets HttpOnly cookies and returns user data
 * 4. Effect dispatches loginSuccess({ user }) or loginFailure({ error })
 * 5. Reducers update state
 * 6. Effect navigates to returnUrl or /dashboard
 */

// Triggered when user submits login form
export const login = createAction(
	'[Auth] Login',
	props<{ credentials: LoginRequest }>()
);

// Dispatched after backend login API succeeds
export const loginSuccess = createAction(
	'[Auth] Login Success',
	props<{ user: User; accessToken: string; refreshToken: string }>()
);

// Dispatched when login fails (invalid credentials, account locked, etc.)
export const loginFailure = createAction(
	'[Auth] Login Failure',
	props<{ error: any }>()
);

/**
 * Logout Actions
 *
 * Flow:
 * 1. Component dispatches logout()
 * 2. User state immediately set to UNAUTHENTICATED
 * 3. All state (user, permissions, etc.) immediately cleared
 * 4. User immediately redirected to /login
 * 5. Backend logout API called in background (result logged, not blocking)
 * 6. Firebase token unregistered in background
 */

// Triggered when user clicks logout button or session is invalid
// This action immediately clears all state and redirects to /login
export const logout = createAction('[Auth] Logout');

/**
 * Initialize Session Actions
 *
 * Flow:
 * 1. App component dispatches initializeSession() on startup
 * 2. Effect calls backend /v2/users/me API (verifies cookies and gets user data)
 * 3. If valid, backend returns user data
 * 4. Effect dispatches initializeSessionSuccess({ user })
 * 5. If invalid/expired, backend returns 401
 * 6. Interceptor tries to refresh token automatically
 * 7. If refresh succeeds, retry /v2/users/me
 * 8. If refresh fails, effect dispatches initializeSessionFailure
 */

// Triggered when app initializes (new tab, page refresh)
export const initializeSession = createAction('[Auth] Initialize Session');

// Dispatched when session is valid and user data is loaded
export const initializeSessionSuccess = createAction(
	'[Auth] Initialize Session Success',
	props<{ user: User }>()
);

// Dispatched when session is invalid (no cookies or refresh failed)
export const initializeSessionFailure = createAction(
	'[Auth] Initialize Session Failure',
	props<{ error: any }>()
);

/**
 * Return URL Actions
 *
 * Flow:
 * 1. AuthGuard detects unauthenticated user trying to access protected route
 * 2. AuthGuard dispatches setReturnUrl({ returnUrl: '/original/route' })
 * 3. AuthGuard redirects to /login?returnUrl=/original/route
 * 4. After successful login, effect reads returnUrl from store
 * 5. Effect navigates to returnUrl or /dashboard
 * 6. Effect dispatches clearReturnUrl() to clean up
 */

// Set the return URL for post-login redirect
export const setReturnUrl = createAction(
	'[Auth] Set Return URL',
	props<{ returnUrl: string | null }>()
);

// Clear the return URL (called after successful navigation)
export const clearReturnUrl = createAction('[Auth] Clear Return URL');
